import { ChildDto, UserDto } from '@ecdlink/core';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import localForage from 'localforage';
import {
  generateCaregiverChildToken,
  refreshCaregiverChildToken,
  openAccessAddChildDetail,
  getChildrenForCoach,
  openAccessAddChild,
  getChildren,
  updateChild,
} from './children.actions';
import { ChildrenState } from './children.types';

const initialState: ChildrenState = {
  children: undefined,
  childUser: undefined,
};

const childrenSlice = createSlice({
  name: 'children',
  initialState,
  reducers: {
    resetChildrenState: (state) => {
      state.children = initialState.children;
      state.childUser = initialState.childUser;
    },
    markOfflineDeactivatedChildSynced: (
      state,
      action: PayloadAction<{ childId: string }>
    ) => {
      if (!state.children) return;

      const childIndex = state.children.findIndex(
        (child) => child.id === action.payload.childId
      );

      if (childIndex < 0) return;

      const child = state.children[childIndex];
      state.children[childIndex] = {
        ...child,
        isOfflineDeactivation: false,
        isOnline: navigator.onLine,
        synced: true,
      };
    },
    createChild: (state, action: PayloadAction<ChildDto>) => {
      if (!state.children) state.children = [];
      const isOnline = navigator.onLine;
      const payloadUpdated = { ...action.payload, isOnline, synced: false };
      state.children?.push(payloadUpdated);
    },
    updateChild: (state, action: PayloadAction<ChildDto>) => {
      if (!state.children) return;

      const isOnline = navigator.onLine;
      const payloadUpdated = { ...action.payload, isOnline };

      const childIndex = state.children.findIndex(
        (child) => child.id === action.payload.id
      );

      if (childIndex < 0) return;

      state.children[childIndex] = { ...payloadUpdated, synced: false };
    },
    createChildUser: (state, action: PayloadAction<UserDto>) => {
      const isOnline = navigator.onLine;
      const payloadUpdated = { ...action.payload, isOnline, synced: false };

      if (!state.childUser) state.childUser = [];
      state.childUser?.push(payloadUpdated);
    },
    updateChildUser: (state, action: PayloadAction<UserDto>) => {
      if (state.childUser) {
        const isOnline = navigator.onLine;
        const payloadUpdated = { ...action.payload, isOnline, synced: false };
        for (let i = 0; i < state.childUser.length; i++) {
          if (state.childUser[i].id === action.payload.id)
            state.childUser[i] = payloadUpdated;
        }
      }
    },
    deactivateChild: (state, action: PayloadAction<ChildDto>) => {
      if (!state.children) return;

      const isOnline = navigator.onLine;
      const payloadUpdated = { ...action.payload, isOnline };

      const childIndex = state.children.findIndex(
        (child) => child.id === action.payload.id
      );

      if (childIndex < 0) return;

      // Record the deactivation before touching the child user record.
      // Offline this slice is the only record the sync has to work from, so a
      // missing or not yet loaded child user must not discard the whole
      // deactivation.
      state.children[childIndex] = { ...payloadUpdated, synced: false };

      const childUserIndex =
        state.childUser?.findIndex(
          (child) => child.id === action.payload.userId
        ) ?? -1;

      if (state.childUser && childUserIndex >= 0) {
        state.childUser[childUserIndex].isActive = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getChildren.fulfilled, (state, action) => {
      if (!state.children) {
        const childUsers: UserDto[] = [];

        for (let i = 0; i < action.payload.length; i++) {
          if (action.payload[i].user) {
            childUsers.push(action.payload[i].user as UserDto);
          }
        }

        state.childUser = childUsers?.map((childUser) => ({
          ...childUser,
          synced: true,
        }));
        state.children = action.payload.map((child) => ({
          ...child,
          synced: true,
        }));
      }
    });
    builder.addCase(updateChild.fulfilled, (state, action) => {
      if (!state.children) return;

      const childIndex = state.children.findIndex(
        (child) => child.id === action.payload.id
      );

      if (childIndex < 0) return;

      state.children[childIndex] = { ...action.payload, synced: true };
    });
    builder.addCase(
      generateCaregiverChildToken.fulfilled,
      (state, action) => {}
    );
    builder.addCase(
      refreshCaregiverChildToken.fulfilled,
      (state, action) => {}
    );
    builder.addCase(getChildrenForCoach.fulfilled, (state, action) => {
      if (!state.children || !state.childUser) {
        const childUsers: UserDto[] = [];

        for (let i = 0; i < action.payload.length; i++) {
          if (action.payload[i].user) {
            childUsers.push(action.payload[i].user as UserDto);
          }
        }

        state.childUser = childUsers?.map((childUser) => ({
          ...childUser,
          synced: true,
        }));
        state.children = action.payload.map((child) => ({
          ...child,
          synced: true,
        }));
      }
    });
    builder.addCase(openAccessAddChildDetail.fulfilled, (state, action) => {});
    builder.addCase(openAccessAddChild.fulfilled, (state, action) => {});
  },
});
const { reducer: childrenReducer, actions: childrenActions } = childrenSlice;

const childrenPersistConfig = {
  key: 'children',
  storage: localForage,
  blacklist: [],
};

export { childrenPersistConfig, childrenReducer, childrenActions };
